import { apiUrl } from '@configs/environment';
import httpClient from '@shared/api/httpClient';
import { ImageUrls } from '@shared/enums/ImageUrls';
import { AxiosResponse } from 'axios';
import { ClientProjectData } from '../interfaces/ClientProjectData';
import { ClientContactData } from '../interfaces/ClientSubProjectData';
import { ClientWorkstreamData } from '../interfaces/ClientWorkstreamData';
import { AddClietContactData, ClietContactFormData } from '../interfaces/ClietContactFormData';
import {
  ClientContactResponseData,
  ClientWorkstreamsResponseData,
  ClientWorkstreamContactsResponseData,
  RawClientWorkstreamEntry,
} from './types';
import { ClientsTypeOfContacts } from '../enums/ClientsTypeOfContact';
import { YesNo } from '@shared/enums/yesNo';

function mapContactsFromResponse(contacts: ClientWorkstreamContactsResponseData[]): ClientContactData[] {
  return contacts.map(
    ({
      zoho_id,
      first_name,
      last_name,
      do_not_touch,
      title,
      email_ae,
      account_name,
      work_stream_id,
      technical_person,
      feedback_giver,
      location,
      location_area,
      rm_type_of_contact,
      rm_contact_status,
      csat_giver,
      contact_owner,
      is_registered_in_client_dashboard,
      client_dashboard_teams,
      client_dashboard_user_id,
      client_dashboard_permissions,
    }) => ({
      id: zoho_id,
      firstName: first_name || 'No name',
      lastName: last_name || 'No name',
      title: title || '',
      accountName: account_name,
      workstream: (work_stream_id?.filter((x) => x !== null) as string[]) || [],
      emailAeTool: email_ae || '',
      technicalPerson: technical_person === YesNo.Yes,
      feedbackGiver: feedback_giver,
      csatGiver: csat_giver,
      location: location || '',
      customTitle: '',
      locationArea: location_area || '',
      rmTypeOfContact:
        rm_type_of_contact && rm_type_of_contact !== 'null' ? rm_type_of_contact : ClientsTypeOfContacts.client,
      rmContactStatus: rm_contact_status,
      imageUrl: ImageUrls.Client,
      doNotTouch: do_not_touch,
      contactOwner: contact_owner,
      isRegisteredInClientDashboard: is_registered_in_client_dashboard ?? false,
      clientDashboardTeams: client_dashboard_teams,
      clientDashboardUserId: client_dashboard_user_id,
      clientDashboardPermissions: client_dashboard_permissions,
    })
  );
}

const mapWorkstream = (ws: RawClientWorkstreamEntry): ClientWorkstreamData => ({
  name: ws.name,
  zoho_crm_id: ws.zoho_crm_id,
  isWorkstream: ws.is_workstream,
  status: ws.status,
  contacts: ws.contacts ? mapContactsFromResponse(ws.contacts) : [],
});

/** Client departments, each with its DB workstreams (no contacts — rows load those lazily). */
export const fetchProjectListData = (): Promise<ClientProjectData[]> =>
  httpClient.get<ClientContactResponseData>('/my-projects-list/').then((resp) =>
    resp.data.data.map(({ zoho_crm_id, ae_customer_id, workstreams, ...restValues }) => ({
      ...restValues,
      zohoCrmId: zoho_crm_id,
      customerId: ae_customer_id,
      workstreams: (workstreams ?? []).map(mapWorkstream),
    }))
  );

/** Zoho's view of one client's workstreams (+ pseudo-workstream contacts): adds the rows the DB lacks. */
export const fetchClientWorkstreamsData = (
  departmentId: string
): Promise<ClientProjectData & { workstreams: ClientWorkstreamData[] }> =>
  httpClient
    .get<ClientWorkstreamsResponseData>(`/clients/${departmentId}/workstreams`)
    .then(({ data: { data: department } }) => {
      const { zoho_crm_id, ae_customer_id, workstreams, ...restValues } = department;
      return {
        ...restValues,
        zohoCrmId: zoho_crm_id,
        customerId: ae_customer_id,
        workstreams: workstreams.map(mapWorkstream),
      };
    });

/** client_id lets the backend serve a workstream the DB lacks, once Zoho confirms it is that client's. */
export const fetchClientWorkstreamContactsData = (
  workstreamId: string,
  clientId: string
): Promise<ClientContactData[]> =>
  httpClient
    .get<{ data: ClientWorkstreamContactsResponseData[] }>(`/${workstreamId}/client-contacts`, {
      params: { client_id: clientId },
    })
    .then((resp) => mapContactsFromResponse(resp.data.data));

export const postClientContact = (
  payload: AddClietContactData,
  shouldCreateDashboardUser: boolean
): Promise<AxiosResponse> => {
  const {
    firstName,
    lastName,
    title,
    customTitle,
    workstream,
    emailAeTool,
    technicalPerson,
    location,
    locationArea,
    rmTypeOfContact,
    rmContactStatus,
    doNotTouch,
    feedbackGiver,
    csatGiver,
    zohoCrmId,
    accountName,
    clientDashboardPermissions,
    clientDashboardTeams,
  } = payload;

  return httpClient.post<void>(`${apiUrl}/client-contacts/`, {
    first_name: firstName,
    last_name: lastName,
    title: title !== 'Other' ? title : customTitle,
    work_stream_id: workstream,
    email_ae: emailAeTool,
    technical_person: technicalPerson ? YesNo.Yes : YesNo.No,
    feedback_giver: feedbackGiver,
    location: location,
    location_area: locationArea,
    rm_type_of_contact: rmTypeOfContact,
    rm_contact_status: rmContactStatus,
    do_not_touch: doNotTouch,
    csat_giver: csatGiver,
    zoho_crm_id: zohoCrmId,
    should_create_dashboard_user: shouldCreateDashboardUser,
    client_dashboard_permissions: clientDashboardPermissions,
    client_dashboard_teams: clientDashboardTeams,
    account_name: accountName,
  });
};

export const patchClientContact = async (
  payload: ClietContactFormData,
  shouldCreateDashboardUser: boolean
): Promise<AxiosResponse<{ client_dashboard_user_id?: string }>> => {
  const {
    firstName,
    lastName,
    title,
    customTitle,
    workstream,
    emailAeTool,
    technicalPerson,
    location,
    locationArea,
    rmTypeOfContact,
    rmContactStatus,
    doNotTouch,
    feedbackGiver,
    csatGiver,
    id,
    accountName,
    clientDashboardTeams,
    clientDashboardUserId,
    clientDashboardPermissions,
  } = payload;

  return httpClient.patch<{ client_dashboard_user_id?: string }>(`${apiUrl}/client-contacts/`, {
    first_name: firstName,
    last_name: lastName,
    title: title !== 'Other' ? title : customTitle,
    work_stream_id: workstream,
    email_ae: emailAeTool,
    technical_person: technicalPerson ? YesNo.Yes : YesNo.No,
    feedback_giver: feedbackGiver,
    location: location,
    location_area: locationArea,
    rm_type_of_contact: rmTypeOfContact,
    rm_contact_status: rmContactStatus,
    do_not_touch: doNotTouch,
    csat_giver: csatGiver,
    zoho_id: id,
    should_create_dashboard_user: shouldCreateDashboardUser,
    account_name: accountName,
    client_dashboard_permissions: clientDashboardPermissions,
    client_dashboard_teams: clientDashboardTeams,
    client_dashboard_user_id: clientDashboardUserId,
  });
};
